import * as React from "react";
import type { DonationRequestItem } from "../../../../services/donationRequestService";
import type { RequestFileRow } from "./DonationRequestBase";
import LandDonationPostSubmitTimeline from "./LandDonationPostSubmitTimeline";
import { normalizeLandRequestStage } from "../../../../utils/donationRequestUiStages";

type Props = {
  t: (k: string, defaultValueOrOptions?: any, options?: any) => string;

  // table
  requests: DonationRequestItem[];
  selectedReq: DonationRequestItem | null;
  onSelect: (r: DonationRequestItem) => void;
  loadingRequests: boolean;
  numberSar: (v: number | null | undefined) => string;
  stageLabel: (stage?: string | null, state?: string | null) => string;
  hideRequestsTable?: boolean;

  // summary / actions
  isDraft: boolean;
  isDirectDonor: boolean;
  hasFinancial: boolean;
  hasPoA: boolean;
  requirementsMet: boolean;
  confirmChecked: boolean;
  onConfirmChange: (v: boolean) => void;
  onSubmitRequest: () => void;
  submittingReq: boolean;
  onDelete: () => void;

  // uploads
  docType: number;
  setDocType: (v: number) => void;
  uploadBusy: boolean;
  uploadErr: string | null;
  setUploadErr: (s: string | null) => void;
  MAX_MB: number;
  pendingFiles: Record<number, File | null>;
  setPendingFile: (type: number, file: File | null) => void;
  filesLoading: boolean;
  files: RequestFileRow[];
  previews: Record<number, string>;
  downloadByDonor: (f: RequestFileRow) => Promise<void>;

  // final alerts
  submitErr: string | null;
  submitOk: string | null;

  // post-submit navigation (optional)
  onGoToDashboard?: () => void;
  onGoToMyRequests?: () => void;

  // view mode (pre-submit flow)
  mode?: "documents" | "send";
  onNext?: () => void;
};

const DOC = { FinancialCapacity: 1, PowerOfAttorney: 2 } as const;

/** يطابق غلاف بطاقات LandInfoPanel / ApplicantPanel */
const mainCardClass =
  "w-full overflow-hidden rounded-3xl border border-neutral-200/90 bg-white shadow-lg shadow-neutral-200/40 ring-1 ring-black/[0.03]";
/** مثل `landCardHeaderClass` في LandInfoPanel */
const sectionMainTitleClass =
  "border-b border-neutral-200/80 bg-gradient-to-l from-sa-50/90 via-white to-white px-5 py-3.5 text-center text-base font-semibold text-neutral-900";
const headerBarClass =
  "border-b border-neutral-200/80 bg-gradient-to-l from-sa-50/90 via-white to-white px-5 py-3.5";
const innerShellClass =
  "overflow-hidden rounded-2xl border border-neutral-200/90 bg-gradient-to-br from-neutral-50/70 via-white to-white shadow-inner";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-neutral-100 py-2.5 last:border-0 sm:grid-cols-[minmax(7.5rem,10rem)_1fr] sm:items-baseline sm:gap-4">
      <dt className="text-sm font-medium text-neutral-500">{label}</dt>
      <dd className="text-sm font-semibold leading-relaxed text-neutral-900 break-words">{value}</dd>
    </div>
  );
}

const nextCtaClass =
  "inline-flex items-center justify-center min-h-[48px] min-w-[200px] rounded-2xl bg-sa-600 px-10 py-3 text-sm font-semibold text-white shadow-md shadow-sa-600/25 transition hover:bg-sa-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

const RequestsSection: React.FC<Props> = (props) => {
  const {
    t,
    requests,
    selectedReq,
    onSelect,
    loadingRequests,
    numberSar,
    stageLabel,
    hideRequestsTable = false,

    isDraft,
    isDirectDonor,
    hasFinancial,
    hasPoA,
    requirementsMet,
    confirmChecked,
    onConfirmChange,
    onSubmitRequest,
    submittingReq,
    onDelete,

    docType,
    setDocType,
    uploadBusy,
    uploadErr,
    setUploadErr,
    MAX_MB,
    pendingFiles,
    setPendingFile,
    filesLoading,
    files,
    previews,
    downloadByDonor,

    submitErr,
    submitOk,
    onGoToDashboard,
    onGoToMyRequests,
    mode = "documents",
    onNext,
  } = props;

  const docTypeLabel = (v: number) =>
    v === DOC.FinancialCapacity
      ? t("Docs:financialCapacity", "خطاب الملاءة المالية")
      : t("Docs:poaImage", "صك التوكيل الشرعي");

  const pendingInputFinancialRef = React.useRef<HTMLInputElement | null>(null);
  const pendingInputPoaRef = React.useRef<HTMLInputElement | null>(null);
  /** حقل الرفع داخل قسم «تحميل المستندات المطلوبة» عند التحرير */
  const uploadFormInputRef = React.useRef<HTMLInputElement | null>(null);
  const declarationRef = React.useRef<HTMLDivElement | null>(null);

  const [requestsTableOpen, setRequestsTableOpen] = React.useState(true);

  React.useEffect(() => {
    // Reduce noise once a submitted request is selected
    if (selectedReq?.tokenReqId && !isDraft) setRequestsTableOpen(false);
  }, [selectedReq?.tokenReqId, isDraft]);

  const pickFileForType = (type: number, e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadErr(null);
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(type, f);
    e.currentTarget.value = "";
    if (uploadFormInputRef.current) uploadFormInputRef.current.value = "";
  };

  const downloadLocalFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {!hideRequestsTable && (
        <div className={mainCardClass}>
          <button
            type="button"
            className={`${headerBarClass} flex w-full items-center justify-between text-right font-semibold text-neutral-900`}
            onClick={() => setRequestsTableOpen((v) => !v)}
            aria-expanded={requestsTableOpen}
          >
            <span>{t("currentRequestsTitle", "طلبات المساهمة الحالية")}</span>
            <span className="text-neutral-500" aria-hidden>
              {requestsTableOpen ? "−" : "+"}
            </span>
          </button>
          {requestsTableOpen && (
            <div className="px-5 py-5 sm:px-6 sm:py-6">
              {!requests && (
                <div className="text-sm text-neutral-500">
                  {t("pickDonorToSeeRequests", "اختر متبرعًا لعرض طلباته")}
                </div>
              )}
              {requests && loadingRequests && (
                <div className="text-sm text-neutral-500">{t("loading", "جاري التحميل...")}</div>
              )}
              {requests && !loadingRequests && requests.length === 0 && (
                <div className="text-sm text-neutral-500">
                  {t("noRequestsForDonor", "لا توجد طلبات مسجلة لهذا المتبرع")}
                </div>
              )}

              {requests && !loadingRequests && requests.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-right text-sm">
                    <thead className="bg-neutral-50">
                      <tr className="text-neutral-600">
                        <th className="px-3 py-2 font-medium">{t("req.code", "رقم الطلب")}</th>
                        <th className="px-3 py-2 font-medium">{t("req.state", "حالة الطلب")}</th>
                        <th className="px-3 py-2 font-medium">{t("req.land", "الأرض")}</th>
                        <th className="px-3 py-2 text-left font-medium">
                          {t("req.amountRequested", "المبلغ المطلوب")}
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          {t("req.amountFinal", "المبلغ النهائي")}
                        </th>
                        <th className="px-3 py-2 text-left font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200">
                      {requests.map((r) => (
                        <tr
                          key={r.tokenReqId}
                          className={`${selectedReq?.tokenReqId === r.tokenReqId ? "bg-neutral-50" : "bg-white"}`}
                        >
                          <td className="px-3 py-2 text-neutral-900">{r.code}</td>
                          <td className="px-3 py-2 text-neutral-900">{stageLabel(r.stageName, r.state)}</td>
                          <td className="px-3 py-2 text-neutral-900">
                            {r.landCode || (r.landId ? `#${r.landId}` : "-")}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1 text-left">
                              <span className="font-medium text-neutral-900">{numberSar(r.requestedAmount)}</span>
                              <span className="inline-flex items-center">
                                <img
                                  className="riyal-icon h-4 w-4"
                                  src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                                  alt="ريال سعودي"
                                  onError={(e) => {
                                    e.currentTarget.replaceWith(
                                      Object.assign(document.createElement("span"), {
                                        textContent: "﷼",
                                        ariaLabel: "ريال سعودي",
                                      }) as unknown as Node
                                    );
                                  }}
                                />
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1 text-left">
                              <span className="font-medium text-neutral-900">{numberSar(r.finalAmount)}</span>
                              <span className="inline-flex items-center">
                                <img
                                  className="riyal-icon h-4 w-4"
                                  src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                                  alt="ريال سعودي"
                                  onError={(e) => {
                                    e.currentTarget.replaceWith(
                                      Object.assign(document.createElement("span"), {
                                        textContent: "﷼",
                                        ariaLabel: "ريال سعودي",
                                      }) as unknown as Node
                                    );
                                  }}
                                />
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <button
                              className="inline-flex items-center rounded-lg border border-sa-200/90 px-3 py-1.5 text-xs font-medium text-sa-800 shadow-sm transition hover:border-sa-300 hover:bg-sa-50/60 focus:outline-none focus:ring-2 focus:ring-sa-500"
                              onClick={() => onSelect(r)}
                              title={t("select", "تحديد")}
                              aria-label={t("select", "تحديد")}
                            >
                              <span aria-hidden="true">✓</span>
                              <span className="sr-only">{t("select", "تحديد")}</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Selected request summary + uploads */}
      {selectedReq && (
        <div className={mainCardClass} dir="rtl">
          <div className={sectionMainTitleClass}>
            {mode === "send"
              ? t("nav.send", "إرسال الطلب")
              : t("documentsSectionTitle", "المستندات والمرفقات")}
          </div>

          <div className="px-5 py-5 sm:px-6 sm:py-6">
            <div className="space-y-5">
            <div className={innerShellClass}>
              <div className="divide-y divide-neutral-200/80">
                <div className="p-4 sm:p-5">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="min-w-0 text-right text-sm font-bold text-neutral-900">
                      {t("summaryTitle", "ملخص الطلب")}
                    </h3>
                    {isDraft ? (
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          className="inline-flex min-h-[40px] items-center justify-center whitespace-nowrap rounded-xl border border-red-200/90 bg-white px-4 py-2 text-sm font-semibold text-red-700 shadow-sm transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                          onClick={onDelete}
                        >
                          {t("deleteRequest", "حذف الطلب")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <dl>
                    <DetailRow
                      label={t("req.code", "رقم الطلب")}
                      value={<span className="font-mono tabular-nums">{selectedReq.code}</span>}
                    />
                    <DetailRow
                      label={t("req.land", "الأرض")}
                      value={selectedReq.landCode || (selectedReq.landId ? `#${selectedReq.landId}` : "—")}
                    />
                    <DetailRow
                      label={t("req.amountRequested", "المبلغ المطلوب")}
                      value={
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          {numberSar(selectedReq.requestedAmount)}
                          <img
                            className="riyal-icon h-4 w-4"
                            src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.replaceWith(
                                Object.assign(document.createElement("span"), {
                                  textContent: "﷼",
                                  ariaLabel: "ريال سعودي",
                                }) as unknown as Node
                              );
                            }}
                          />
                        </span>
                      }
                    />
                    <DetailRow
                      label={t("req.amountFinal", "المبلغ النهائي")}
                      value={
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          {numberSar(selectedReq.finalAmount)}
                          <img
                            className="riyal-icon h-4 w-4"
                            src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.replaceWith(
                                Object.assign(document.createElement("span"), {
                                  textContent: "﷼",
                                  ariaLabel: "ريال سعودي",
                                }) as unknown as Node
                              );
                            }}
                          />
                        </span>
                      }
                    />
                  </dl>
                  {!isDraft && mode === "send" && (
                    <div className="mt-4 border-t border-neutral-200/80 pt-4">
                      <div className="flex flex-col gap-1">
                        <div className="text-xs text-neutral-500">{t("req.state", "حالة الطلب")}</div>
                        <div className="text-base font-bold text-neutral-900">
                          {stageLabel(selectedReq.stageName, selectedReq.state)}
                        </div>
                        <div className="mt-1 text-sm text-neutral-600 leading-relaxed">
                          {(() => {
                            const cat = normalizeLandRequestStage(selectedReq.stageName, selectedReq.state);
                            if (cat === "submitted")
                              return t("postSubmit.help.submitted", "تم استلام طلبك وسيتم تحديث الحالة عند بدء المعالجة.");
                            if (cat === "inProgress")
                              return t("postSubmit.help.inProgress", "طلبك قيد المراجعة حاليًا، ولا يلزمك إجراء في هذه المرحلة.");
                            if (cat === "approved")
                              return t("postSubmit.help.approved", "تم اعتماد طلبك. يمكنك الاحتفاظ بالمستندات للرجوع إليها.");
                            if (cat === "rejected")
                              return t("postSubmit.help.rejected", "لم تتم الموافقة على الطلب. يمكنك مراجعة المستندات والعودة لطلب جديد عند الحاجة.");
                            return t("postSubmit.help.unknown", "تتم متابعة الطلب وتحديث حالته تلقائيًا وفق ما يرد من النظام.");
                          })()}
                        </div>
                        {onGoToMyRequests && (
                          <div className="mt-3">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center rounded-xl border border-sa-600 px-4 py-2 text-sm font-semibold text-sa-700 hover:bg-sa-50"
                              onClick={onGoToMyRequests}
                            >
                              {t("goToMyRequests", "عرض طلباتي")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {(isDraft || mode === "documents") && (
                  <div className="p-4 sm:p-5">
                    <h3 className="mb-3 text-right text-sm font-bold text-neutral-900">
                      {isDirectDonor
                        ? t("requirements.direct", "المتطلبات للمساهم المباشر")
                        : t("requirements.indirect", "المتطلبات للمتبرع غير المباشر")}
                    </h3>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-2">
                      <span
                        className={`inline-flex w-fit max-w-full items-center rounded-full border px-3 py-1.5 text-xs font-semibold leading-snug ${
                          hasFinancial
                            ? "border-emerald-200/80 bg-emerald-50/90 text-emerald-900"
                            : "border-amber-200/80 bg-amber-50/90 text-amber-900"
                        }`}
                      >
                        {t("Docs:financialCapacity", "خطاب الملاءة المالية")} —{" "}
                        {hasFinancial
                          ? t("uploads.statusUploaded", "تم الرفع")
                          : t("uploads.statusMissing", "غير مرفوع")}
                      </span>
                      {!isDirectDonor && (
                        <span
                          className={`inline-flex w-fit max-w-full items-center rounded-full border px-3 py-1.5 text-xs font-semibold leading-snug ${
                            hasPoA
                              ? "border-emerald-200/80 bg-emerald-50/90 text-emerald-900"
                              : "border-amber-200/80 bg-amber-50/90 text-amber-900"
                          }`}
                        >
                          {t("Docs:poaImage", "صك التوكيل الشرعي")} —{" "}
                          {hasPoA
                            ? t("uploads.statusUploaded", "تم الرفع")
                            : t("uploads.statusMissing", "غير مرفوع")}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {mode === "documents" && (
                  <div className={`p-4 sm:p-5 ${!isDraft ? "opacity-95" : ""}`}>
                    <h3 className="mb-4 text-right text-sm font-bold text-neutral-900">
                      {t("uploads.title", "تحميل المستندات المطلوبة")}
                    </h3>
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="block text-right text-sm font-medium text-neutral-700" htmlFor="docs-upload-type">
                          {t("uploads.typeLabel", "نوع المستند")}
                        </label>
                        <select
                          id="docs-upload-type"
                          className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-xs focus:border-sa-500 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:opacity-80"
                          value={docType}
                          onChange={(e) => setDocType(Number(e.target.value))}
                          disabled={!isDraft}
                        >
                          <option value={DOC.FinancialCapacity}>
                            {docTypeLabel(DOC.FinancialCapacity)}
                          </option>
                          <option value={DOC.PowerOfAttorney}>
                            {docTypeLabel(DOC.PowerOfAttorney)}
                          </option>
                        </select>
                      </div>

                      <div className="space-y-3">
                        <label className="block text-right text-sm font-medium text-neutral-700">
                          {t("uploads.fileLabel", "ملف المستند")}
                        </label>
                        <input
                          ref={uploadFormInputRef}
                          type="file"
                          className="hidden"
                          tabIndex={-1}
                          accept=".pdf,.jpg,.jpeg,.png,image/*"
                          disabled={
                            !isDraft ||
                            uploadBusy ||
                            (docType === DOC.FinancialCapacity && hasFinancial) ||
                            (docType === DOC.PowerOfAttorney && hasPoA)
                          }
                          onChange={(e) => pickFileForType(docType, e)}
                        />
                        <div className="rounded-xl border border-neutral-200/90 bg-neutral-50/90 px-3 py-2.5 text-right">
                          <div className="text-sm font-medium text-neutral-900 break-all">
                            {pendingFiles?.[docType]?.name?.trim()
                              ? pendingFiles[docType]!.name
                              : t("uploads.noFileSelectedYet", "لم يُختر ملف بعد")}
                          </div>
                          {!pendingFiles?.[docType]?.name?.trim() ? (
                            <div className="mt-0.5 text-xs text-neutral-500" dir="ltr" lang="en">
                              {t("uploads.noFileSelectedYetEn", "No file selected yet")}
                            </div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-sa-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-sa-600/25 transition hover:bg-sa-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={
                            !isDraft ||
                            uploadBusy ||
                            (docType === DOC.FinancialCapacity && hasFinancial) ||
                            (docType === DOC.PowerOfAttorney && hasPoA)
                          }
                          onClick={() => uploadFormInputRef.current?.click()}
                        >
                          {uploadBusy
                            ? t("uploads.uploading", "جاري الرفع...")
                            : t("uploads.pick", "اختيار الملف")}
                        </button>
                        <p className="text-right text-xs leading-relaxed text-neutral-500">
                          <span className="block" dir="rtl">
                            {t(
                              "uploads.hintAr",
                              "الصيغ المسموح بها: PDF، JPG، PNG (حد أقصى {{max}} ميجابايت)",
                              { max: String(MAX_MB) }
                            )}
                          </span>
                          <span className="mt-1 block text-neutral-400" dir="ltr" lang="en">
                            {t(
                              "uploads.hintEn",
                              "Allowed formats: PDF, JPG, PNG (max {{max}} MB)",
                              { max: String(MAX_MB) }
                            )}
                          </span>
                        </p>
                      </div>
                    </div>

                    {uploadErr && isDraft && (
                      <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-right text-sm text-red-800">
                        {uploadErr}
                      </div>
                    )}
                  </div>
                )}

            {mode === "documents" && (
              <div className="p-4 sm:p-5">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <h3 className="text-right text-sm font-bold text-neutral-900">
                  {t("uploads.existingTitle", "المستندات المرفوعة")}
                </h3>
                {!isDraft ? (
                  <div className="text-right text-xs text-neutral-500">
                    {t("uploads.readOnlyNote", "لا يمكن تعديل المستندات بعد إرسال الطلب")}
                  </div>
                ) : null}
              </div>
              {filesLoading ? (
                <div className="text-sm text-neutral-500">
                  {t("loading", "جاري التحميل...")}
                </div>
              ) : !isDraft ? (
                files.length === 0 ? (
                  <div
                    className="rounded-2xl border border-dashed border-neutral-200/90 bg-neutral-50/60 px-4 py-10 text-center text-sm text-neutral-500"
                    role="status"
                  >
                    {t("uploads.none", "لا توجد مستندات مرفوعة")}
                  </div>
                ) : (
                  <ul className="divide-y divide-neutral-200 rounded-2xl border border-neutral-200">
                    {files.map((f) => (
                      <li key={f.id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          {previews[f.id] ? (
                            <img
                              src={previews[f.id]}
                              alt={f.name || ""}
                              className="h-10 w-10 rounded-lg border border-neutral-200 object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg border border-neutral-200 bg-neutral-50" />
                          )}
                          <span className="text-neutral-900">{f.name?.trim() || docTypeLabel(f.documentTypeId)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-700">
                            {docTypeLabel(f.documentTypeId)}
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center rounded-xl border border-sa-200/90 px-3 py-1.5 text-xs font-medium text-sa-800 shadow-sm transition hover:border-sa-300 hover:bg-sa-50/60 focus:outline-none focus:ring-2 focus:ring-sa-500"
                            onClick={() => downloadByDonor(f)}
                          >
                            {t("uploads.download", "تحميل")}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )
              ) : files.length === 0 ? (
                // Show pending picks (local) even if server list is empty
                (pendingFiles?.[DOC.FinancialCapacity] || pendingFiles?.[DOC.PowerOfAttorney]) ? (
                  <ul className="divide-y divide-neutral-200 rounded-2xl border border-neutral-200">
                    {/* Pending (local) files before upload — show تحميل + تعديل بجانب بعض */}
                    {[DOC.FinancialCapacity, DOC.PowerOfAttorney].map((dt) => {
                      if (dt === DOC.PowerOfAttorney && isDirectDonor) return null;
                      const pf = pendingFiles?.[dt] ?? null;
                      if (!pf) return null;

                      const inputRef = dt === DOC.FinancialCapacity ? pendingInputFinancialRef : pendingInputPoaRef;

                      return (
                        <li
                          key={`pending-${dt}`}
                          className="flex items-center justify-between px-4 py-3"
                          title={t("uploads.pendingNote", "هذا ملف مختار قبل الإرسال وسيتم رفعه عند إرسال الطلب")}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg border border-neutral-200 bg-neutral-50" />
                            <span className="text-neutral-900" title={pf.name}>
                              {docTypeLabel(dt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-700">
                              {docTypeLabel(dt)}
                            </span>

                            <button
                              type="button"
                              className="inline-flex items-center rounded-xl border border-sa-200/90 px-3 py-1.5 text-xs font-medium text-sa-800 shadow-sm transition hover:border-sa-300 hover:bg-sa-50/60 focus:outline-none focus:ring-2 focus:ring-sa-500"
                              onClick={() => downloadLocalFile(pf)}
                            >
                              {t("uploads.download", "تحميل")}
                            </button>

                            <input
                              ref={inputRef}
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png,image/*"
                              onChange={(e) => pickFileForType(dt, e)}
                            />

                            <button
                              type="button"
                              className="inline-flex items-center rounded-xl border border-neutral-300 px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                              onClick={() => inputRef.current?.click()}
                              disabled={uploadBusy}
                            >
                              {t("uploads.editPick", "تعديل")}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div
                    className="rounded-2xl border border-dashed border-neutral-200/90 bg-neutral-50/60 px-4 py-10 text-center text-sm text-neutral-500"
                    role="status"
                  >
                    {t("uploads.none", "لا توجد مستندات مرفوعة")}
                  </div>
                )
              ) : (
                <ul className="divide-y divide-neutral-200 rounded-2xl border border-neutral-200">
                  {/* Pending (local) files before upload — show تحميل + تعديل بجانب بعض */}
                  {[DOC.FinancialCapacity, DOC.PowerOfAttorney].map((dt) => {
                    if (dt === DOC.PowerOfAttorney && isDirectDonor) return null;
                    const pf = pendingFiles?.[dt] ?? null;
                    if (!pf) return null;

                    const inputRef = dt === DOC.FinancialCapacity ? pendingInputFinancialRef : pendingInputPoaRef;

                    return (
                      <li
                        key={`pending-${dt}`}
                        className="flex items-center justify-between px-4 py-3"
                        title={t("uploads.pendingNote", "هذا ملف مختار قبل الإرسال وسيتم رفعه عند إرسال الطلب")}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg border border-neutral-200 bg-neutral-50" />
                            <span className="text-neutral-900" title={pf.name}>
                              {docTypeLabel(dt)}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-700">
                            {docTypeLabel(dt)}
                          </span>

                          <button
                            type="button"
                            className="inline-flex items-center rounded-xl border border-sa-200/90 px-3 py-1.5 text-xs font-medium text-sa-800 shadow-sm transition hover:border-sa-300 hover:bg-sa-50/60 focus:outline-none focus:ring-2 focus:ring-sa-500"
                            onClick={() => downloadLocalFile(pf)}
                          >
                            {t("uploads.download", "تحميل")}
                          </button>

                          <input
                            ref={inputRef}
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,image/*"
                            onChange={(e) => pickFileForType(dt, e)}
                          />

                          <button
                            type="button"
                            className="inline-flex items-center rounded-xl border border-neutral-300 px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploadBusy}
                          >
                            {t("uploads.editPick", "تعديل")}
                          </button>
                        </div>
                      </li>
                    );
                  })}

                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        {/* image preview if available */}
                        {previews[f.id] ? (
                          <img
                            src={previews[f.id]}
                            alt={f.name || ""}
                            className="h-10 w-10 rounded-lg border border-neutral-200 object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg border border-neutral-200 bg-neutral-50" />
                        )}
                        <span className="text-neutral-900">{f.name?.trim() || docTypeLabel(f.documentTypeId)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-700">
                          {docTypeLabel(f.documentTypeId)}
                        </span>
                        <button
                          className="inline-flex items-center rounded-xl border border-sa-200/90 px-3 py-1.5 text-xs font-medium text-sa-800 shadow-sm transition hover:border-sa-300 hover:bg-sa-50/60 focus:outline-none focus:ring-2 focus:ring-sa-500"
                          onClick={() => downloadByDonor(f)}
                        >
                          {t("uploads.download", "تحميل")}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}
              </div>
            </div>

            {/* ما بعد الإرسال: النجاح + مسار المعالجة — حالة الطلب داخل ملخص الطلب أعلاه */}
            {!isDraft && mode === "send" && (
              <div className={`${innerShellClass} px-4 py-4 sm:px-5 sm:py-5`}>
                {submitOk && (
                  <>
                    <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-green-800">
                      {submitOk}
                    </div>
                    {(onGoToDashboard || onGoToMyRequests) && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {onGoToMyRequests && (
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-xl border border-sa-600 px-4 py-2 text-sm font-medium text-sa-700 hover:bg-sa-50"
                            onClick={onGoToMyRequests}
                          >
                            طلباتي
                          </button>
                        )}
                        {onGoToDashboard && (
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-xl border border-neutral-400 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
                            onClick={onGoToDashboard}
                          >
                            الذهاب للوحة المؤشرات
                          </button>
                        )}
                      </div>
                    )}
                    <div className="my-4 h-px w-full bg-neutral-200/70" aria-hidden />
                  </>
                )}
                <LandDonationPostSubmitTimeline stageName={selectedReq.stageName} state={selectedReq.state} />
              </div>
            )}

            {submitErr && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                {submitErr}
              </div>
            )}
            {/* رسالة النجاح + أزرار التنقل: في تبويب الإرسال تُعرض داخل بطاقة مسار المعالجة؛ هنا لحالات أخرى فقط */}
            {submitOk &&
              !(mode === "documents" && !isDraft) &&
              !(mode === "send" && !isDraft) && (
              <>
                <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-green-800">
                  {submitOk}
                </div>
                {(onGoToDashboard || onGoToMyRequests) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {onGoToMyRequests && (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-xl border border-sa-600 px-4 py-2 text-sm font-medium text-sa-700 hover:bg-sa-50"
                        onClick={onGoToMyRequests}
                      >
                        طلباتي
                      </button>
                    )}
                    {onGoToDashboard && (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-xl border border-neutral-400 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
                        onClick={onGoToDashboard}
                      >
                        الذهاب للوحة المؤشرات
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Final submit stage (no modal) */}
            {isDraft && mode === "send" && (
              <div
                ref={declarationRef}
                className={`${innerShellClass} px-4 py-5 sm:px-6 sm:py-6`}
              >
                <div className="flex flex-col gap-3">
                  <label className="flex items-start gap-3 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-neutral-300 text-sa-600 focus:ring-sa-500"
                      checked={confirmChecked}
                      onChange={(e) => onConfirmChange(e.target.checked)}
                      disabled={!requirementsMet}
                    />
                    <span>
                      {t(
                        "declarationText",
                        "أقر بأن جميع البيانات والمستندات التي تم إدخالها صحيحة"
                      )}
                    </span>
                  </label>
                  <button
                    className="inline-flex w-full items-center justify-center min-h-[44px] rounded-xl bg-sa-600 px-5 py-3 text-sm font-semibold text-white hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={onSubmitRequest}
                    disabled={!requirementsMet || !confirmChecked || submittingReq}
                  >
                    {submittingReq ? t("submitting", "جاري الإرسال...") : t("submit", "إرسال الطلب")}
                  </button>
                </div>
              </div>
            )}

            {/* Next button at bottom of documents stage */}
            {isDraft && mode === "documents" && onNext && (
              <div className="rounded-3xl border border-neutral-200/80 bg-gradient-to-b from-white to-neutral-50/80 px-5 py-6 shadow-md shadow-neutral-200/30 sm:px-8">
                <div className="flex justify-center">
                  <button type="button" className={nextCtaClass} disabled={!requirementsMet} onClick={onNext}>
                    {t("next", "التالي")}
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestsSection;
